import { KpiDelta } from '../kpi-delta';
import type { ContractEffort, EacEvaluationWire, EacMode, SlipUnit } from '@forecastify/shared';
import { eacFormula, eacHint, eacLabel, eacMenuItems } from '../eac-mode';
import { DropdownMenu, Icon } from '@forecastify/ui';
import { useT } from '../../../i18n';
import { FinanceTip, type FinanceTipText } from '../finance/finance-tip';
import { barWidth, hours, index, percent, signedCount, signedHours } from './effort-format';
import type { EffortModel } from './effort-model';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ C65 · the tiles, the frame bars and the list panel — in HOURS.            │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THREE TILE SETS, AND THEY ARE NOT COSMETIC VARIANTS OF ONE.** A retainer with a period
 * picked is asked *"is this month going to fit"* — the period's frame, the daily rate, the
 * projected overrun. The same retainer whole is asked *"is the series holding"* — the contract
 * frame, the carry-over, the plan. A one-off project is asked *"will it land"* — completion, CPI,
 * slip. One merged set would put a daily run rate on a project with no period to run through, and
 * a slip figure on a retainer that has no deadline to slip against.
 *
 * ⚠️ **The measurements are `finance-panels.tsx`'s, deliberately.** The two tabs sit one click
 * apart on the same project; a 14px track here against a 12px track there is the kind of
 * difference a reader feels without being able to name it.
 */

const DESTRUCTIVE = 'text-destructive';
const TIME_BAR = 'color-mix(in oklch, var(--foreground) 28%, transparent)';

function bad(condition: boolean): string {
  return condition ? DESTRUCTIVE : '';
}

/* ── the tiles ─────────────────────────────────────────────────────────────── */

interface Tile {
  readonly key: string;
  readonly label: string;
  readonly value: string;
  readonly valueClass?: string;
  readonly formula?: string;
  readonly tip: FinanceTipText;
  readonly bar?: { readonly pct: number; readonly color: string };
  /** ⚠️ The second, fainter bar is ELAPSED TIME, so the two lengths can be compared at a glance. */
  readonly timeBar?: number;
  /** A unit that is itself a control — *munkanap* or *naptári nap* on the slip tile. */
  readonly menu?: readonly { label: string; checked: boolean; onSelect: () => void }[];
}

export function EffortKpis({
  view,
  model,
  slipUnit,
  onSlipUnit,
  live = null,
}: {
  readonly view: ContractEffort;
  readonly model: EffortModel;
  readonly slipUnit: SlipUnit;
  readonly onSlipUnit: (unit: SlipUnit) => void;
  /** 30 Oct 2026 · today's figures, under a past day's — *Eltérés a jelenhez*. */
  readonly live?: { readonly view: ContractEffort; readonly model: EffortModel } | null;
}) {
  const t = useT();
  /* 30 Oct 2026 · the tiles, for whichever figures — the shown day's, and today's beside them. */
  const tilesOf = (view: ContractEffort, model: EffortModel): Tile[] => {
    const mn = (count: number): string => t('effort.workdayShort', { count: String(count) });

    const slipMenu = [
      {
        label: t('effort.slip.workday'),
        checked: slipUnit === 'workday',
        onSelect: () => {
          onSlipUnit('workday');
        },
      },
      {
        label: t('effort.slip.calendar'),
        checked: slipUnit === 'calendar',
        onSelect: () => {
          onSlipUnit('calendar');
        },
      },
    ];

    const spent = model.spentShare;
    const overSpent = spent !== null && spent > 1;

    /** The tile every variant draws in second place: consumption against the frame, over elapsed time. */
    const spentTile = (key: string, label: string, tip: FinanceTipText): Tile => ({
      key,
      label,
      value: percent(spent),
      valueClass: bad(overSpent),
      formula: `${hours(model.logged)} / ${hours(model.budget)}`,
      tip,
      bar: {
        pct: barWidth(model.logged, model.budget),
        color: overSpent ? 'var(--destructive)' : 'var(--primary)',
      },
      timeBar: Math.min(100, model.elapsedShare * 100),
    });

    const elapsedTile = (key: string, label: string, tip: FinanceTipText): Tile => ({
      key,
      label,
      value: percent(model.elapsedShare),
      formula: `${String(model.workdaysDone)} / ${mn(model.workdays)}`,
      tip,
      bar: { pct: model.elapsedShare * 100, color: TIME_BAR },
    });

    const billableTile: Tile = {
      key: 'billable',
      label: t('effort.kpi.billable'),
      value: hours(model.billable),
      formula: t('effort.kpi.billableFormula'),
      tip: {
        name: t('effort.tip.billable.name'),
        what: t('effort.tip.billable.what'),
        calc: t('effort.tip.billable.calc'),
        note: t('effort.tip.billable.note'),
      },
    };

    const occurrenceTiles: Tile[] = [
      {
        key: 'frame',
        label: t('effort.kpi.periodFrame'),
        value: hours(model.budget),
        formula: t('effort.kpi.periodFrameFormula', {
          total: hours(model.budgetTotal),
          count: String(view.periodCount),
        }),
        tip: {
          name: t('effort.tip.periodFrame.name'),
          what: t('effort.tip.periodFrame.what'),
          calc: t('effort.tip.periodFrame.calc'),
          note: t('effort.tip.periodFrame.note'),
        },
      },
      spentTile('spent', t('effort.kpi.periodSpent'), {
        name: t('effort.tip.periodSpent.name'),
        what: t('effort.tip.periodSpent.what'),
        calc: t('effort.tip.periodSpent.calc'),
        note: t('effort.tip.periodSpent.note'),
      }),
      elapsedTile('elapsed', t('effort.kpi.periodElapsed'), {
        name: t('effort.tip.elapsed.name'),
        what: t('effort.tip.periodElapsed.what'),
        calc: t('effort.tip.elapsed.calc'),
        note: t('effort.tip.periodElapsed.note'),
      }),
      {
        key: 'rate',
        label: t('effort.kpi.rate'),
        value: hours(model.rate),
        valueClass: bad(model.rate > model.allowedRate + 0.004),
        formula: `${hours(model.logged)} / ${mn(model.workdaysDone)}`,
        tip: {
          name: t('effort.tip.rate.name'),
          what: t('effort.tip.rate.what'),
          calc: t('effort.tip.rate.calc'),
          note: t('effort.tip.rate.note'),
        },
      },
      {
        key: 'overrun',
        label: t('effort.kpi.overrun'),
        value: signedHours(model.overrun),
        valueClass: bad(model.overrun > 0.004),
        formula: `${hours(model.projected)} − ${hours(model.budget)}`,
        tip: {
          name: t('effort.tip.overrun.name'),
          what: t('effort.tip.overrun.what'),
          calc: t('effort.tip.overrun.calc'),
          note: t('effort.tip.overrun.note'),
        },
      },
    ];

    const seriesTiles: Tile[] = [
      {
        key: 'frame',
        label: t('effort.kpi.contractFrame'),
        value: hours(model.budgetTotal),
        formula: view.hasBillable
          ? t('effort.kpi.contractFrameRetainer')
          : t('effort.kpi.contractFrameAll'),
        tip: {
          name: t('effort.tip.bac.name'),
          what: t('effort.tip.contractFrame.what'),
          calc: t('effort.tip.contractFrame.calc'),
          note: t('effort.tip.contractFrame.note'),
        },
      },
      spentTile('spent', t('effort.kpi.spent'), {
        name: t('effort.tip.spent.name'),
        what: t('effort.tip.contractSpent.what'),
        calc: t('effort.tip.contractSpent.calc'),
        note: t('effort.tip.contractSpent.note'),
      }),
      elapsedTile('elapsed', t('effort.kpi.elapsed'), {
        name: t('effort.tip.elapsed.name'),
        what: t('effort.tip.contractElapsed.what'),
        calc: t('effort.tip.elapsed.calc'),
        note: t('effort.tip.contractElapsed.note'),
      }),
      {
        key: 'planned',
        label: t('effort.kpi.planned'),
        value: hours(model.planned),
        formula: t('effort.kpi.plannedFormula'),
        tip: {
          name: t('effort.tip.plan.name'),
          what: t('effort.tip.plan.what'),
          calc: t('effort.tip.plan.calc'),
          note: t('effort.tip.plan.note'),
        },
      },
      {
        key: 'carry',
        label: t('effort.kpi.carryOver'),
        value: signedHours(model.carryOver),
        valueClass: bad(model.carryOver < -0.004),
        formula: t('effort.kpi.carryOverFormula'),
        tip: {
          name: t('effort.tip.carryOver.name'),
          what: t('effort.tip.carryOver.what'),
          calc: t('effort.tip.carryOver.calc'),
          note: t('effort.tip.carryOver.note'),
        },
      },
    ];

    const done = model.doneShare;
    const plainTiles: Tile[] = [
      {
        key: 'frame',
        label: t('effort.kpi.frame'),
        value: hours(model.budget),
        formula: t('effort.kpi.frameFormula'),
        tip: {
          name: t('effort.tip.bac.name'),
          what: t('effort.tip.frame.what'),
          calc: t('effort.tip.frame.calc'),
        },
      },
      {
        key: 'spent',
        label: t('effort.kpi.spent'),
        value: percent(spent),
        valueClass: bad(overSpent),
        formula: `${hours(model.logged)} / ${hours(model.budget)}`,
        tip: {
          name: t('effort.tip.spent.name'),
          what: t('effort.tip.spent.what'),
          calc: t('effort.tip.spent.calc'),
          note: t('effort.tip.spent.note'),
        },
        bar: {
          pct: barWidth(model.logged, model.budget),
          color: overSpent ? 'var(--destructive)' : 'var(--primary)',
        },
      },
      {
        key: 'done',
        label: t('effort.kpi.done'),
        value: percent(done),
        formula: `${hours(model.earned)} / ${hours(model.budget)}`,
        tip: {
          name: t('effort.tip.done.name'),
          what: t('effort.tip.done.what'),
          calc: t('effort.tip.done.calc'),
          note: t('effort.tip.done.note'),
        },
        /* ⚠️ Green only at a FULL hundred percent — the mockup's own green, `--success`, and the one
           place on this screen where a colour says *finished* rather than *within budget*. */
        bar: {
          pct: barWidth(model.earned, model.budget),
          color: done !== null && done >= 1 ? 'var(--success)' : 'var(--primary)',
        },
      },
      elapsedTile('elapsed', t('effort.kpi.elapsed'), {
        name: t('effort.tip.elapsed.name'),
        what: t('effort.tip.plainElapsed.what'),
        calc: t('effort.tip.elapsed.calc'),
        note: t('effort.tip.plainElapsed.note'),
      }),
      {
        key: 'cpi',
        label: t('effort.kpi.cpi'),
        value: index(model.cpi),
        valueClass: bad(model.cpi !== null && model.cpi < 0.92),
        formula: 'EV / AC',
        tip: {
          name: t('effort.tip.cpi.name'),
          what: t('effort.tip.cpi.what'),
          calc: t('effort.tip.cpi.calc'),
          note: t('effort.tip.cpi.note'),
        },
      },
      {
        key: 'slip',
        label: t('effort.kpi.slip'),
        value: signedCount(
          slipUnit === 'workday' ? model.slipWorkdays : model.slipDays,
          slipUnit === 'workday' ? t('effort.unit.workday') : t('effort.unit.day'),
        ),
        valueClass: bad(
          (slipUnit === 'workday' ? model.slipWorkdays : model.slipDays) !== null &&
            (slipUnit === 'workday' ? (model.slipWorkdays ?? 0) : (model.slipDays ?? 0)) > 0,
        ),
        formula: t('effort.kpi.slipFormula'),
        menu: slipMenu,
        tip: {
          name: t('effort.tip.slip.name'),
          what: t('effort.tip.slip.what'),
          calc: t('effort.tip.slip.calc'),
          note: t('effort.tip.slip.note'),
        },
      },
    ];

    const base =
      model.variant === 'occurrence'
        ? occurrenceTiles
        : model.variant === 'series'
          ? seriesTiles
          : plainTiles;
    /* ⚠️ The over-frame tile is absent where there IS no over-frame work — a permanent `0:00` on a
       contract that can never have any is furniture, and it would push a real tile off the row. */
    const tiles =
      view.hasBillable && model.variant !== 'plain' ? [...base, billableTile] : [...base];
    return tiles;
  };
  const tiles = tilesOf(view, model);
  const todays = live === null ? null : tilesOf(live.view, live.model);

  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-3 xl:grid-cols-6">
      {tiles.map((tile) => (
        /**
         * ┌─────────────────────────────────────────────────────────────────────────────────────┐
         * │ ⚠️⚠️ THE ORDER IS LABEL → VALUE → FORMULA → BARS, AND IT IS THE FIX RATHER THAN A   │
         * │    REARRANGEMENT.                                                                   │
         * └─────────────────────────────────────────────────────────────────────────────────────┘
         *
         * Ours read label → value → bars → formula, and every attempt to line the captions up
         * fought that: `TÉNY / KERET` draws two rules, `ELTELT IDŐ` one, three of the six none, so
         * with the bars ABOVE it each caption landed at a height set by whatever furniture happened
         * to sit over it. Pinning the caption to the bottom of the card papered over it — the
         * baselines matched and the bars then disagreed instead.
         *
         * The mockup puts the formula directly under the value at a fixed `mt-2` and hangs the bars
         * below it. Every caption is then at the same height by construction, with nothing pinned,
         * and the bars are free to differ in number because nothing reads across them.
         */
        <div key={tile.key} className="bg-card rounded-xl border p-3 shadow-sm">
          <div className="text-muted-foreground flex items-center gap-1.5 text-[11px] font-medium tracking-wide uppercase">
            {tile.menu === undefined ? (
              <FinanceTip tip={tile.tip} calcLabel={t('finance.calc')}>
                {tile.label}
              </FinanceTip>
            ) : (
              <DropdownMenu
                width={260}
                align="start"
                label={t('effort.slip.label')}
                items={tile.menu.map((item) => ({
                  label: item.label,
                  checked: item.checked,
                  onSelect: item.onSelect,
                }))}
              >
                <button
                  type="button"
                  className="hover:text-foreground flex items-center gap-1 uppercase transition-colors"
                >
                  {tile.label}
                  <Icon name="chevron-down" size={11} className="opacity-60" />
                </button>
              </DropdownMenu>
            )}
          </div>

          {/* ⚠️ 22px, the mockup's own. Ours was 20 — it reads as a caption beside a 22px one. */}
          <div
            className={`mt-2 text-[22px] leading-none font-medium tabular-nums ${tile.valueClass ?? ''}`}
          >
            {tile.value}
          </div>
          <KpiDelta now={todays?.find((one) => one.key === tile.key)?.value} then={tile.value} />

          {/**
           * ┌────────────────────────────────────────────────────────────────────┐
           * │ ⚠️⚠️ THE FORMULA AND THE BARS ARE GONE — round 5 item 11.                 │
           * └────────────────────────────────────────────────────────────────────┘
           *
           * *"Kártyákról cím és érték kivételével minden lekertül: small text, barok."*
           *
           * ⚠️⚠️ **AND A LONG ARGUMENT ABOUT THEIR ORDER GOES WITH THEM.** The note above used to
           * explain why the formula sits under the value and the bars under that — six captions
           * that would not line up until the order was label → value → formula → bars. It was a
           * real fix for a real problem and it is moot: with neither on the card there is nothing
           * left to align.
           *
           * ⚠️ **`tile.formula` and `tile.bar` are still BUILT**, deliberately. They are computed
           * in `effort-model.ts` from figures the panel needs anyway, the shapes are exercised by
           * that file's tests, and a tile is one prop away from carrying them again. What changed
           * is what this card DRAWS.
           */}
        </div>
      ))}
    </div>
  );
}

/* ── the frame bars ────────────────────────────────────────────────────────── */

interface BarRow {
  readonly key: string;
  readonly label: string;
  readonly value: number;
  readonly color: string;
  readonly className?: string;
  readonly tip: FinanceTipText;
  /** The EAC row's formula switch, drawn as its label — as the finance panel does. */
  readonly menu?: true;
}

export function FramePanel({
  model,
  eac = null,
  eacMode,
  onEacMode,
}: {
  readonly model: EffortModel;
  /** 30 Oct 2026 · EV · the five, evaluated by the server. */
  readonly eac?: EacEvaluationWire | null;
  readonly eacMode: EacMode;
  readonly onEacMode: (mode: EacMode) => void;
}) {
  const t = useT();

  const occurrenceRows: BarRow[] = [
    {
      key: 'frame',
      label: t('effort.bar.frame'),
      value: model.budget,
      color: 'color-mix(in oklch, var(--foreground) 18%, transparent)',
      tip: {
        name: t('effort.tip.periodFrame.name'),
        what: t('effort.tip.periodFrame.what'),
        calc: t('effort.tip.periodFrame.calc'),
        note: t('effort.tip.bar.frameNote'),
      },
    },
    {
      key: 'plan',
      label: t('effort.bar.plan'),
      value: model.planned,
      color: 'color-mix(in oklch, var(--primary) 30%, transparent)',
      tip: {
        name: t('effort.tip.plan.name'),
        what: t('effort.tip.plan.what'),
        calc: t('effort.tip.periodPlan.calc'),
        note: t('effort.tip.bar.planNote'),
      },
    },
    {
      key: 'actual',
      label: t('effort.bar.actual'),
      value: model.logged,
      color: model.logged > model.budget + 0.004 ? 'var(--destructive)' : 'var(--primary)',
      className: bad(model.logged > model.budget + 0.004),
      tip: {
        name: t('effort.tip.actual.name'),
        what: t('effort.tip.actual.what'),
        calc: t('effort.tip.periodActual.calc'),
        note: t('effort.tip.bar.actualNote'),
      },
    },
    {
      key: 'projected',
      label: t('effort.bar.projected'),
      value: model.projected,
      color:
        model.projected > model.budget + 0.004
          ? 'color-mix(in oklch, var(--destructive) 45%, transparent)'
          : 'color-mix(in oklch, var(--primary) 22%, transparent)',
      className: bad(model.projected > model.budget + 0.004),
      tip: {
        name: t('effort.tip.projected.name'),
        what: t('effort.tip.projected.what'),
        calc: t('effort.tip.projected.calc'),
        note: t('effort.tip.projected.note'),
      },
    },
  ];

  const seriesRows: BarRow[] = [
    {
      key: 'plan',
      label: t('effort.bar.plan'),
      value: model.planned,
      color: 'color-mix(in oklch, var(--primary) 30%, transparent)',
      tip: {
        name: t('effort.tip.plan.name'),
        what: t('effort.tip.plan.what'),
        calc: t('effort.tip.plan.calc'),
        note: t('effort.tip.bar.planTotalNote'),
      },
    },
    {
      key: 'actual',
      label: t('effort.bar.actual'),
      value: model.logged,
      color: model.logged > model.budget + 0.004 ? 'var(--destructive)' : 'var(--primary)',
      className: bad(model.logged > model.budget + 0.004),
      tip: {
        name: t('effort.tip.actual.name'),
        what: t('effort.tip.contractActual.what'),
        calc: t('effort.tip.actual.calc'),
        note: t('effort.tip.bar.actualTotalNote'),
      },
    },
    /**
     * ⚠️⚠️ **THE FRAME THAT HAS ACTUALLY OPENED, WHICH IS NOT THE CONTRACT'S.** A twelve-month
     * retainer six months in has only released half its hours, and a `TÉNY` bar read against the
     * full frame looks comfortable in exactly the month it stops being so.
     */
    {
      key: 'closedFrame',
      label: t('effort.bar.closedFrame'),
      value: model.closedBudget,
      color: 'color-mix(in oklch, var(--foreground) 20%, transparent)',
      tip: {
        name: t('effort.tip.closedFrame.name'),
        what: t('effort.tip.closedFrame.what'),
        calc: t('effort.tip.closedFrame.calc'),
        note: t('effort.tip.closedFrame.note'),
      },
    },
  ];

  const plainRows: BarRow[] = [
    {
      key: 'plan',
      label: t('effort.bar.plan'),
      value: model.planned,
      color: 'color-mix(in oklch, var(--primary) 30%, transparent)',
      tip: {
        name: t('effort.tip.plan.name'),
        what: t('effort.tip.plainPlan.what'),
        calc: t('effort.tip.plainPlan.calc'),
        note: t('effort.tip.bar.planScopeNote'),
      },
    },
    {
      key: 'actual',
      label: t('effort.bar.actual'),
      value: model.logged,
      color: model.logged > model.budget + 0.004 ? 'var(--destructive)' : 'var(--primary)',
      className: bad(model.logged > model.budget + 0.004),
      tip: {
        name: t('effort.tip.actual.name'),
        what: t('effort.tip.plainActual.what'),
        calc: t('effort.tip.actual.calc'),
        note: t('effort.tip.bar.actualScopeNote'),
      },
    },
    {
      key: 'earned',
      label: t('effort.bar.earned'),
      value: model.earned,
      color: 'color-mix(in oklch, var(--primary) 60%, transparent)',
      tip: {
        name: t('effort.tip.ev.name'),
        what: t('effort.tip.ev.what'),
        calc: t('effort.tip.ev.calc'),
        note: t('effort.tip.ev.note'),
      },
    },
    {
      key: 'eac',
      menu: true,
      label: t('eac.row', { mode: eacLabel(t, eacMode) }),
      value: model.eac ?? 0,
      color:
        (model.eac ?? 0) > model.budget + 0.004
          ? 'color-mix(in oklch, var(--destructive) 45%, transparent)'
          : 'color-mix(in oklch, var(--primary) 22%, transparent)',
      className: bad((model.eac ?? 0) > model.budget + 0.004),
      tip: {
        name: eacLabel(t, eacMode),
        what: eacHint(t, eacMode),
        calc: eacFormula(eacMode),
      },
    },
  ];

  const rows =
    model.variant === 'occurrence'
      ? occurrenceRows
      : model.variant === 'series'
        ? seriesRows
        : plainRows;

  /* ⚠️ One scale for every bar, a little wider than the largest figure — so an overrun visibly
     overhangs the frame mark rather than being clipped flush with it. */
  const scale = Math.max(1, ...rows.map((row) => row.value), model.budget) * 1.06;
  const share = (value: number): string =>
    model.budget > 0.004 ? `${String(Math.round((value / model.budget) * 100))}%` : '–';

  const occurrenceFoot = [
    {
      key: 'rate',
      label: t('effort.foot.rate'),
      value: hours(model.rate),
      className: bad(model.rate > model.allowedRate + 0.004),
      tip: {
        name: t('effort.tip.rate.name'),
        what: t('effort.tip.rate.what'),
        calc: t('effort.tip.rate.calc'),
      },
    },
    {
      key: 'allowedRate',
      label: t('effort.foot.allowedRate'),
      value: hours(model.allowedRate),
      className: '',
      tip: {
        name: t('effort.tip.allowedRate.name'),
        what: t('effort.tip.allowedRate.what'),
        calc: t('effort.tip.allowedRate.calc'),
      },
    },
    {
      key: 'rateDelta',
      label: t('effort.foot.rateDelta'),
      value: signedHours(model.rateDelta),
      className: bad(model.rateDelta > 0.004),
      tip: {
        name: t('effort.tip.rateDelta.name'),
        what: t('effort.tip.rateDelta.what'),
        calc: t('effort.tip.rateDelta.calc'),
        note: t('effort.tip.rateDelta.note'),
      },
    },
    {
      key: 'remaining',
      label: t('effort.foot.remaining'),
      value: signedHours(model.remaining),
      className: bad(model.remaining < -0.004),
      tip: {
        name: t('effort.tip.remaining.name'),
        what: t('effort.tip.remaining.what'),
        calc: t('effort.tip.remaining.calc'),
        note: t('effort.tip.remaining.note'),
      },
    },
    {
      key: 'burnOut',
      label: t('effort.foot.burnOut'),
      value: model.burnOutOn ?? '–',
      /* ⚠️ Red when the frame runs out BEFORE the period does — that is the whole point of it. */
      className: bad(model.burnOutOn !== null && model.burnOutOn < model.deadline),
      tip: {
        name: t('effort.tip.burnOut.name'),
        what: t('effort.tip.burnOut.what'),
        calc: t('effort.tip.burnOut.calc'),
        note: t('effort.tip.burnOut.note'),
      },
    },
    {
      key: 'workdaysLeft',
      label: t('effort.foot.workdaysLeft'),
      value: String(model.workdaysLeft),
      className: '',
      tip: {
        name: t('effort.tip.workdaysLeft.name'),
        what: t('effort.tip.periodWorkdaysLeft.what'),
        calc: t('effort.tip.workdaysLeft.calc'),
      },
    },
  ];

  const seriesFoot = [
    {
      key: 'periodBudget',
      label: t('effort.foot.periodBudget'),
      value: hours(model.periodBudget),
      className: '',
      tip: {
        name: t('effort.tip.periodFrame.name'),
        what: t('effort.tip.periodFrame.what'),
        calc: t('effort.tip.periodFrame.calc'),
        note: t('effort.tip.periodFrame.note'),
      },
    },
    {
      key: 'averageUsage',
      label: t('effort.foot.averageUsage'),
      value: hours(model.averageUsage),
      className: bad(model.averageBudget > 0.004 && model.averageUsage > model.averageBudget),
      tip: {
        name: t('effort.tip.averageUsage.name'),
        what: t('effort.tip.averageUsage.what'),
        calc: t('effort.tip.averageUsage.calc'),
        note: t('effort.tip.averageUsage.note'),
      },
    },
    {
      key: 'remaining',
      label: t('effort.foot.remainingFrame'),
      value: signedHours(model.remaining),
      className: bad(model.remaining < -0.004),
      tip: {
        name: t('effort.tip.remaining.name'),
        what: t('effort.tip.contractRemaining.what'),
        calc: t('effort.tip.contractRemaining.calc'),
        note: t('effort.tip.remaining.note'),
      },
    },
    {
      key: 'periodsLeft',
      label: t('effort.foot.periodsLeft'),
      value: String(model.periodsLeft),
      className: '',
      tip: {
        name: t('effort.tip.periodsLeft.name'),
        what: t('effort.tip.periodsLeft.what'),
        calc: t('effort.tip.periodsLeft.calc'),
      },
    },
    {
      key: 'overtime',
      label: t('effort.foot.overtime'),
      value: hours(model.overtime),
      className: model.overtime > 0.004 ? '' : 'text-muted-foreground',
      tip: {
        name: t('effort.tip.overtime.name'),
        what: t('effort.tip.overtime.what'),
        calc: t('effort.tip.overtime.calc'),
      },
    },
    {
      key: 'workdaysLeft',
      label: t('effort.foot.workdaysLeft'),
      value: String(model.workdaysLeft),
      className: '',
      tip: {
        name: t('effort.tip.workdaysLeft.name'),
        what: t('effort.tip.contractWorkdaysLeft.what'),
        calc: t('effort.tip.workdaysLeft.calc'),
      },
    },
  ];

  const plainFoot = [
    {
      key: 'etc',
      label: t('effort.foot.etc'),
      value: model.etc === null ? '–' : hours(model.etc),
      className: '',
      tip: {
        name: t('effort.tip.etc.name'),
        what: t('effort.tip.etc.what'),
        calc: 'EAC − AC',
      },
    },
    {
      key: 'vac',
      label: t('effort.foot.vac'),
      value: signedHours(model.vac),
      className: bad(model.vac !== null && model.vac < -0.004),
      tip: {
        name: t('effort.tip.vac.name'),
        what: t('effort.tip.vac.what'),
        calc: 'BAC − EAC',
        note: t('effort.tip.vac.note'),
      },
    },
    {
      key: 'tcpi',
      label: t('effort.foot.tcpi'),
      value: index(model.tcpi),
      className: bad(model.tcpi !== null && model.tcpi > 1),
      tip: {
        name: t('effort.tip.tcpi.name'),
        what: t('effort.tip.tcpi.what'),
        calc: '(BAC − EV) / (BAC − AC)',
        note: t('effort.tip.tcpi.note'),
      },
    },
    {
      key: 'spi',
      label: t('effort.foot.spi'),
      value: index(model.spi),
      className: bad(model.spi !== null && model.spi < 0.92),
      tip: {
        name: t('effort.tip.spi.name'),
        what: t('effort.tip.spi.what'),
        calc: 'EV / PV',
        note: t('effort.tip.spi.note'),
      },
    },
    {
      key: 'cv',
      label: t('effort.foot.cv'),
      value: signedHours(model.costVariance),
      className: bad(model.costVariance < -0.004),
      tip: {
        name: t('effort.tip.cv.name'),
        what: t('effort.tip.cv.what'),
        calc: 'EV − AC',
        note: t('effort.tip.cv.note'),
      },
    },
    {
      key: 'sv',
      label: t('effort.foot.sv'),
      value: signedHours(model.scheduleVariance),
      className: bad(model.scheduleVariance < -0.004),
      tip: {
        name: t('effort.tip.sv.name'),
        what: t('effort.tip.sv.what'),
        calc: 'EV − PV',
        note: t('effort.tip.sv.note'),
      },
    },
    {
      key: 'overtime',
      label: t('effort.foot.overtime'),
      value: hours(model.overtime),
      className: model.overtime > 0.004 ? '' : 'text-muted-foreground',
      tip: {
        name: t('effort.tip.overtime.name'),
        what: t('effort.tip.overtime.what'),
        calc: t('effort.tip.overtime.calc'),
      },
    },
  ];

  const foot =
    model.variant === 'occurrence'
      ? occurrenceFoot
      : model.variant === 'series'
        ? seriesFoot
        : plainFoot;

  return (
    <div className="bg-card rounded-xl border p-4 shadow-sm">
      <div className="flex items-center gap-2">
        <div className="text-muted-foreground text-[11px] font-medium tracking-wide uppercase">
          <FinanceTip
            calcLabel={t('finance.calc')}
            tip={{
              name: t('effort.tip.panel.name'),
              what: t('effort.tip.panel.what'),
              note: t('effort.tip.panel.note'),
            }}
          >
            {t('effort.panel.frame')}
          </FinanceTip>
        </div>
      </div>

      <div className="relative mt-4 flex flex-col gap-5">
        {rows.map((row) => (
          <div
            key={row.key}
            className="grid grid-cols-[minmax(0,176px)_minmax(0,1fr)_132px_42px] items-center gap-2 phone:grid-cols-[88px_minmax(48px,1fr)_auto_36px]"
          >
            <span
              className={`text-muted-foreground truncate text-[11px] font-medium tracking-wide uppercase ${row.className ?? ''}`}
            >
              {row.menu === undefined ? (
                <FinanceTip tip={row.tip} calcLabel={t('finance.calc')}>
                  {row.label}
                </FinanceTip>
              ) : (
                /* ⚠️ The EAC formula is a reading of the same data, so its switch IS the bar's
                   label — the finance panel's pattern. It used to be a separate dropdown under the
                   bars, which read as a control for the whole card. */
                <DropdownMenu
                  width={320}
                  align="start"
                  label={t('finance.eac.label')}
                  items={eacMenuItems(t, eac, eacMode, onEacMode)}
                >
                  <button
                    type="button"
                    className="hover:text-foreground flex max-w-full items-center gap-1 uppercase transition-colors"
                  >
                    <span className="truncate">{row.label}</span>
                    <Icon name="chevron-down" size={11} className="shrink-0 opacity-60" />
                  </button>
                </DropdownMenu>
              )}
            </span>
            {/**
             * ⚠️⚠️ **NO WIDTH CAP ON THE TRACK, WHICH IS WHAT MADE THESE BARS UNREADABLE.** The
             * cap came across from the money panel, where six short bars sit beside a wide figure
             * column. Here the panel is the width of the screen and the track was pinned to 300px
             * inside it, so a bar at 49% of its scale was drawn at a sixth of the row — every
             * length looked like a rounding error and the frame mark sat in the middle of nowhere.
             * The mockup runs the track the full width of the panel; the percentages were right
             * all along, the ruler they were drawn on was not.
             */}
            <span className="bg-muted/40 relative h-[14px] min-w-0 rounded-sm">
              <span
                className="absolute inset-y-0 left-0 rounded-sm"
                style={{ width: `${String(barWidth(row.value, scale))}%`, background: row.color }}
              />
              <span
                aria-hidden
                className="bg-foreground/70 absolute inset-y-[-3px] w-[2px]"
                style={{ left: `${String(barWidth(model.budget, scale))}%` }}
              />
            </span>
            <span
              className={`text-right text-[12.5px] font-medium tabular-nums ${row.className ?? ''}`}
            >
              {hours(row.value)}
            </span>
            <span className="text-muted-foreground text-right text-[11.5px] tabular-nums">
              {share(row.value)}
            </span>
          </div>
        ))}
      </div>

      <div className="mt-3 grid grid-cols-1 gap-x-6 gap-y-1 border-t pt-3 sm:grid-cols-2">
        {foot.map((note) => (
          <span
            key={note.key}
            className="flex items-center gap-1.5 border-b border-dashed py-1 text-[11.5px] last:border-0"
          >
            <span className="text-muted-foreground min-w-0 truncate">
              <FinanceTip tip={note.tip} calcLabel={t('finance.calc')}>
                {note.label}
              </FinanceTip>
            </span>
            <span className="flex-1" />
            <span className={`shrink-0 text-[12.5px] font-medium tabular-nums ${note.className}`}>
              {note.value}
            </span>
          </span>
        ))}
      </div>
    </div>
  );
}

/* ── the list panel: SOROZAT for a retainer, ÜTEM for a one-off ────────────── */

export function EffortListPanel({
  view,
  model,
}: {
  readonly view: ContractEffort;
  readonly model: EffortModel;
}) {
  const t = useT();
  const recurring = view.recurring;

  const seriesRows = [
    {
      key: 'closedPeriods',
      label: t('effort.list.closedPeriods'),
      value: String(model.closedPeriods),
      className: '',
      tip: {
        name: t('effort.tip.closedPeriods.name'),
        what: t('effort.tip.closedPeriods.what'),
        calc: t('effort.tip.closedPeriods.calc'),
      },
    },
    {
      key: 'averageUsage',
      label: t('effort.list.averageUsage'),
      value: hours(model.averageUsage),
      className: '',
      tip: {
        name: t('effort.tip.averageUsage.name'),
        what: t('effort.tip.averageUsage.what'),
        calc: t('effort.tip.averageUsage.calc'),
      },
    },
    {
      key: 'averageShare',
      label: t('effort.list.averageShare'),
      value: percent(model.averageBudget > 0.004 ? model.averageUsage / model.averageBudget : null),
      className: bad(model.averageBudget > 0.004 && model.averageUsage > model.averageBudget),
      tip: {
        name: t('effort.tip.averageShare.name'),
        what: t('effort.tip.averageShare.what'),
        calc: t('effort.tip.averageShare.calc'),
        note: t('effort.tip.averageShare.note'),
      },
    },
    {
      key: 'carryOver',
      label: t('effort.list.carryOver'),
      value: signedHours(model.carryOver),
      className: bad(model.carryOver < -0.004),
      tip: {
        name: t('effort.tip.carryOver.name'),
        what: t('effort.tip.carryOver.what'),
        calc: t('effort.tip.carryOver.calc'),
        note: t('effort.tip.carryOver.note'),
      },
    },
    {
      key: 'periodsLeft',
      label: t('effort.list.periodsLeft'),
      value: String(model.periodsLeft),
      className: '',
      tip: {
        name: t('effort.tip.periodsLeft.name'),
        what: t('effort.tip.periodsLeft.what'),
        calc: t('effort.tip.periodsLeft.calc'),
      },
    },
    {
      key: 'nextPlan',
      label: t('effort.list.nextPlan'),
      value: model.nextPlan === null ? '–' : hours(model.nextPlan),
      className: '',
      tip: {
        name: t('effort.tip.nextPlan.name'),
        what: t('effort.tip.nextPlan.what'),
        calc: t('effort.tip.nextPlan.calc'),
      },
    },
    {
      key: 'nextPlanVariance',
      label: t('effort.list.nextPlanVariance'),
      value: signedHours(model.nextPlanVariance),
      className: bad(model.nextPlanVariance !== null && model.nextPlanVariance > 0.004),
      tip: {
        name: t('effort.tip.nextPlanVariance.name'),
        what: t('effort.tip.nextPlanVariance.what'),
        calc: t('effort.tip.nextPlanVariance.calc'),
        note: t('effort.tip.nextPlanVariance.note'),
      },
    },
    ...(view.hasBillable
      ? [
          {
            key: 'billable',
            label: t('effort.list.billable'),
            value: hours(model.billable),
            className: '',
            tip: {
              name: t('effort.tip.billable.name'),
              what: t('effort.tip.billable.what'),
              calc: t('effort.tip.billable.calc'),
              note: t('effort.tip.billable.note'),
            },
          },
        ]
      : []),
  ];

  const scheduleRows = [
    {
      key: 'deadline',
      label: t('effort.list.deadline'),
      value: model.deadline,
      className: '',
      tip: { name: t('effort.tip.deadline.name'), what: t('effort.tip.deadline.what') },
    },
    {
      key: 'forecastFinish',
      label: t('effort.list.forecastFinish'),
      value: model.forecastFinishOn ?? '–',
      className: bad(model.forecastFinishOn !== null && model.forecastFinishOn > model.deadline),
      tip: {
        name: t('effort.tip.forecastFinish.name'),
        what: t('effort.tip.forecastFinish.what'),
        calc: t('effort.tip.forecastFinish.calc'),
        note: t('effort.tip.forecastFinish.note'),
      },
    },
    {
      key: 'workdaysLeft',
      label: t('effort.list.workdaysLeft'),
      value: String(model.workdaysLeft),
      className: '',
      tip: {
        name: t('effort.tip.workdaysLeft.name'),
        what: t('effort.tip.deadlineWorkdaysLeft.what'),
        calc: t('effort.tip.workdaysLeft.calc'),
      },
    },
    {
      key: 'slip',
      label: t('effort.list.slip'),
      value: signedCount(model.slipWorkdays, t('effort.unit.workday')),
      className: bad(model.slipWorkdays !== null && model.slipWorkdays > 0),
      tip: {
        name: t('effort.tip.slip.name'),
        what: t('effort.tip.slip.what'),
        calc: t('effort.tip.slip.calc'),
        note: t('effort.tip.slip.note'),
      },
    },
    {
      key: 'lateTasks',
      label: t('effort.list.lateTasks'),
      value: String(model.lateTasks),
      className: bad(model.lateTasks > 0),
      tip: {
        name: t('effort.tip.lateTasks.name'),
        what: t('effort.tip.lateTasks.what'),
        calc: t('effort.tip.lateTasks.calc'),
      },
    },
    {
      key: 'lateHours',
      label: t('effort.list.lateHours'),
      value: hours(model.lateHours),
      className: bad(model.lateHours > 0.004),
      tip: {
        name: t('effort.tip.lateHours.name'),
        what: t('effort.tip.lateHours.what'),
        calc: t('effort.tip.lateHours.calc'),
        note: t('effort.tip.lateHours.note'),
      },
    },
    {
      key: 'openTaskAge',
      label: t('effort.list.openTaskAge'),
      value: t('effort.workdayShort', { count: String(model.openTaskAge) }),
      className: '',
      tip: {
        name: t('effort.tip.openTaskAge.name'),
        what: t('effort.tip.openTaskAge.what'),
        calc: t('effort.tip.openTaskAge.calc'),
        note: t('effort.tip.openTaskAge.note'),
      },
    },
  ];

  const rows = recurring ? seriesRows : scheduleRows;

  return (
    <div className="bg-card rounded-xl border p-4 shadow-sm">
      <div className="text-muted-foreground text-[11px] font-medium tracking-wide uppercase">
        <FinanceTip
          calcLabel={t('finance.calc')}
          tip={
            recurring
              ? { name: t('effort.tip.series.name'), what: t('effort.tip.series.what') }
              : { name: t('effort.tip.schedule.name'), what: t('effort.tip.schedule.what') }
          }
        >
          {recurring ? t('effort.panel.series') : t('effort.panel.schedule')}
        </FinanceTip>
      </div>
      <div className="mt-1.5 flex flex-col">
        {rows.map((row) => (
          <div key={row.key} className="flex items-center gap-1.5 border-b py-[6px] last:border-0">
            <span className="text-muted-foreground min-w-0 truncate text-[10.5px] font-medium tracking-wide uppercase">
              <FinanceTip tip={row.tip} calcLabel={t('finance.calc')}>
                {row.label}
              </FinanceTip>
            </span>
            <div className="flex-1" />
            <span className={`shrink-0 text-[12.5px] font-medium tabular-nums ${row.className}`}>
              {row.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
